// src/pages/Home.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { dealAPI, categoryAPI } from "../services/api";
import DealCard from "../components/deals/DealCard";
import Loading from "../components/common/Loading";
import FlowerDecor from "../components/common/FlowerDecor";

import { FiShoppingBag, FiPercent, FiMapPin, FiChevronLeft, FiChevronRight } from "react-icons/fi";

import normalizeImagePath from "../utils/imageUtils";
import { renderCategoryIcon } from "../utils/categoryIcons";

const HERO_BG = "/image.jpg";

const readTotalFromResponse = (res) => {
  try {
    const payload = res?.data ?? {};
    if (payload.totalItems ?? payload.total) return Number(payload.totalItems ?? payload.total);
    if (payload.meta && (payload.meta.total ?? payload.meta.totalItems)) return Number(payload.meta.total ?? payload.meta.totalItems);
    if (payload.pagination && (payload.pagination.total ?? payload.pagination.totalItems)) return Number(payload.pagination.total ?? payload.pagination.totalItems);
    if (payload.data && (payload.data.total ?? payload.data.totalItems)) return Number(payload.data.total ?? payload.data.totalItems);
    const headerTotal = res?.headers?.["x-total-count"] || res?.headers?.["X-Total-Count"];
    if (headerTotal) return Number(headerTotal);
  } catch {}
  return 0;
};

const normalizeDealsImages = (list) =>
  (Array.isArray(list) ? list : []).map((d) => {
    try {
      const copy = { ...d };
      if (Array.isArray(copy.images) && copy.images.length > 0) {
        copy.images = copy.images
          .map((img) => {
            try {
              return normalizeImagePath(img) || img;
            } catch {
              return img;
            }
          })
          .filter(Boolean);
      } else {
        copy.images = [];
      }
      return copy;
    } catch {
      return d;
    }
  });

const getDiscountPercent = (deal) => {
  const direct = Number(deal?.discountPercentage);
  if (Number.isFinite(direct) && direct > 0) return direct;

  const original = Number(deal?.originalPrice ?? deal?.priceOriginal ?? 0);
  const discounted = Number(deal?.discountedPrice ?? deal?.price ?? 0);

  if (original > 0 && discounted >= 0 && discounted <= original) {
    return Math.round(((original - discounted) / original) * 100);
  }
  return 0;
};

const pickCityFromDeal = (deal) => {
  const fromLocations = Array.isArray(deal?.locations) && deal.locations.length ? deal.locations[0] : "";
  const fromLocation = deal?.location || "";

  const raw = String(fromLocations || fromLocation || "").trim();
  if (!raw) return "";

  const first = raw.split(",")[0].trim();
  return first.replace(/\s+/g, " ");
};

const Home = () => {
  const [categories, setCategories] = useState([]);
  const [featuredDeals, setFeaturedDeals] = useState([]);

  const [allDeals, setAllDeals] = useState([]);
  const [allLoading, setAllLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [total, setTotal] = useState(0);

  const [topLoading, setTopLoading] = useState(true);

  // ✅ CATEGORIES: 2x2 en mobile => 4 par vue, sinon 8
  const [catIndex, setCatIndex] = useState(0);
  const [catPerView, setCatPerView] = useState(window.innerWidth < 640 ? 4 : 8);

  // ✅ CITIES (5 par view)
  const [cityIndex, setCityIndex] = useState(0);
  const CITY_PER_VIEW = 5;

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // ✅ update catPerView on resize
  useEffect(() => {
    const onResize = () => setCatPerView(window.innerWidth < 640 ? 4 : 8);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // TOP: categories + featured
  const loadTop = useCallback(async (signal) => {
    setTopLoading(true);
    try {
      const [catRes, featRes] = await Promise.allSettled([
        categoryAPI.getAll(),
        dealAPI.getAll({ sort: "newest", limit: 80, page: 1 })
      ]);

      if (catRes.status === "fulfilled") {
        const c = catRes.value?.data ?? catRes.value;
        const clist = (c?.data ?? c) || [];
        if (!signal?.aborted && mountedRef.current) {
          const arr = Array.isArray(clist) ? clist : [];
          setCategories(arr);
          setCatIndex(0);
        }
      } else {
        toast.error("Impossible de charger les catégories");
      }

      if (featRes.status === "fulfilled") {
        const d = featRes.value?.data ?? featRes.value;
        const list = (d?.data ?? d) || [];
        const normalized = normalizeDealsImages(list);

        const best3 = normalized
          .map((x) => ({ ...x, _promo: getDiscountPercent(x) }))
          .filter((x) => (x._promo ?? 0) > 0)
          .sort((a, b) => (b._promo ?? 0) - (a._promo ?? 0))
          .slice(0, 3);

        if (!signal?.aborted && mountedRef.current) {
          setFeaturedDeals(best3);
        }
      } else {
        toast.error("Impossible de charger les offres en vedette");
      }
    } catch {
      toast.error("Erreur lors du chargement de la page");
    } finally {
      if (!signal?.aborted && mountedRef.current) setTopLoading(false);
    }
  }, []);

  const loadAllDeals = useCallback(
    async (p = page, l = limit) => {
      setAllLoading(true);
      try {
        const res = await dealAPI.getAll({ sort: "newest", page: p, limit: l });
        const payload = res?.data ?? res;
        const list = Array.isArray(payload?.data) ? payload.data : Array.isArray(payload) ? payload : [];
        const normalized = normalizeDealsImages(list);

        if (!mountedRef.current) return;

        setAllDeals(normalized);
        const t = readTotalFromResponse(res);
        setTotal(t || normalized.length || 0);

        const serverPage = payload.currentPage ?? payload.page ?? payload.data?.currentPage ?? null;
        const serverLimit = payload.itemsPerPage ?? payload.limit ?? payload.data?.itemsPerPage ?? null;
        if (serverPage) setPage(Number(serverPage));
        if (serverLimit) setLimit(Number(serverLimit));
      } catch (err) {
        console.error("Erreur chargement deals home:", err);
        toast.error("Erreur lors du chargement des offres");
        if (mountedRef.current) {
          setAllDeals([]);
          setTotal(0);
        }
      } finally {
        if (mountedRef.current) setAllLoading(false);
      }
    },
    [page, limit]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadTop(controller.signal);
    return () => controller.abort();
  }, [loadTop]);

  useEffect(() => {
    loadAllDeals(page, limit);
  }, [loadAllDeals, page, limit]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || allDeals.length) / (limit || 1))),
    [total, allDeals.length, limit]
  );

  const goToPage = (p) => {
    const next = Math.max(1, Math.min(totalPages, p));
    if (next === page) return;
    setPage(next);
  };

  // categories visibles
  const visibleCategories = useMemo(() => {
    return (categories || []).slice(catIndex, catIndex + catPerView);
  }, [categories, catIndex, catPerView]);

  const canPrevCat = catIndex > 0;
  const canNextCat = catIndex + catPerView < (categories?.length || 0);

  const prevCats = () => setCatIndex((prev) => Math.max(0, prev - catPerView));
  const nextCats = () => {
    setCatIndex((prev) => {
      const maxStart = Math.max(0, (categories?.length || 0) - catPerView);
      return Math.min(maxStart, prev + catPerView);
    });
  };

  // ========= CITIES + COUNT =========
  const citiesWithCount = useMemo(() => {
    const src = [...(featuredDeals || []), ...(allDeals || [])];
    const map = new Map();

    for (const d of src) {
      const city = pickCityFromDeal(d);
      if (!city) continue;

      const key = city.toLowerCase();
      if (!map.has(key)) map.set(key, { name: city, count: 0 });
      map.get(key).count += 1;
    }

    return Array.from(map.values()).sort((a, b) => {
      const dc = (b.count || 0) - (a.count || 0);
      if (dc !== 0) return dc;
      return String(a.name).localeCompare(String(b.name));
    });
  }, [allDeals, featuredDeals]);

  useEffect(() => {
    setCityIndex(0);
  }, [citiesWithCount.length]);

  const visibleCities = useMemo(() => {
    return citiesWithCount.slice(cityIndex, cityIndex + CITY_PER_VIEW);
  }, [citiesWithCount, cityIndex]);

  const canPrevCity = cityIndex > 0;
  const canNextCity = cityIndex + CITY_PER_VIEW < citiesWithCount.length;

  const prevCities = () => setCityIndex((prev) => Math.max(0, prev - CITY_PER_VIEW));
  const nextCities = () => {
    setCityIndex((prev) => {
      const maxStart = Math.max(0, citiesWithCount.length - CITY_PER_VIEW);
      return Math.min(maxStart, prev + CITY_PER_VIEW);
    });
  };

  return (
    <div className="bg-white">
      <FlowerDecor />

      {/* ✅ HERO (MOBILE + DESKTOP) */}
      <section className="relative w-full overflow-hidden">
        {/* MOBILE HERO */}
        <div
          className="md:hidden relative h-[420px] w-full"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        >
          <div className="absolute inset-0 bg-[#e11d7b]/75" />
          <div className="relative z-10 h-full flex items-center">
            <div className="px-4 w-full">
              <h1 className="text-3xl font-extrabold leading-tight text-white">
                Découvrez les meilleurs deals
                <br />
                au Maroc
              </h1>

              <p className="mt-3 text-sm text-white/90 max-w-[320px]">
                Économisez jusqu’à 90% sur les restaurants, spa, voyages et bien plus encore
              </p>

              <div className="mt-5">
                <Link
                  to="/deals"
                  className="w-full inline-flex items-center justify-center px-5 py-3 rounded-xl bg-white text-[#e11d7b] font-semibold shadow"
                >
                  Explorer les Deals
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* DESKTOP HERO (ton design) */}
        <div className="hidden md:block relative w-full h-[520px]">
          <div className="absolute inset-0">
            <div
              className="absolute right-0 top-0 h-full"
              style={{
                width: "62%",
                backgroundImage: `url(${HERO_BG})`,
                backgroundSize: "cover",
                backgroundPosition: "center"
              }}
            />
            <div
              className="absolute top-0 h-full"
              style={{
                left: "36%",
                width: "1000px",
                background:
                  "linear-gradient(to right, #e11d7b 0%, rgba(225,29,123,0.6) 40%, rgba(225,29,123,0.2) 70%, transparent 100%)"
              }}
            />
          </div>

          <div className="relative z-10 h-full flex">
            <div
              className="flex items-center text-white"
              style={{
                width: "48%",
                minWidth: 0,
                backgroundColor: "#e11d7b",
                clipPath: "polygon(0 0, 100% 0, 95% 30%, 80% 100%, 0 100%)"
              }}
            >
              <div className="max-w-xl pl-16 pr-10">
                <h1 className="text-5xl font-extrabold leading-tight">
                  Découvrez les meilleurs deals
                  <br />
                  au Maroc
                </h1>

                <p className="mt-4 text-lg text-white/90">
                  Économisez jusqu’à 90% sur les restaurants, spa, voyages et bien plus encore
                </p>

                <div className="mt-6">
                  <Link
                    to="/deals"
                    className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-white text-[#e11d7b] font-semibold shadow hover:shadow-lg transition"
                  >
                    Explorer les Deals
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section className="py-10 bg-[#fff3f8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-2xl font-bold text-center text-gray-900">
            Comment ça marche
          </h2>
          <p className="text-center text-sm text-gray-500 mt-1">
            Découvrez comment profiter des meilleures offres en 3 étapes simples
          </p>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-white shadow flex items-center justify-center">
                <FiShoppingBag className="text-[#e11d7b]" size={26} />
              </div>
              <h3 className="mt-3 font-semibold">1. Trouvez votre deal</h3>
              <p className="text-sm text-gray-600 mt-1">Parcourez nos offres par catégories</p>
            </div>

            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-white shadow flex items-center justify-center">
                <FiPercent className="text-[#e11d7b]" size={26} />
              </div>
              <h3 className="mt-3 font-semibold">2. Achetez en ligne</h3>
              <p className="text-sm text-gray-600 mt-1">Payez en ligne et recevez votre bon</p>
            </div>

            <div className="text-center">
              <div className="mx-auto w-14 h-14 rounded-full bg-white shadow flex items-center justify-center">
                <FiMapPin className="text-[#e11d7b]" size={26} />
              </div>
              <h3 className="mt-3 font-semibold">3. Profitez de l’offre</h3>
              <p className="text-sm text-gray-600 mt-1">Présentez votre voucher et profitez</p>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ CATEGORIES */}
      <section id="categories" className="py-10 bg-[#fde7ef]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-2xl font-bold text-center text-[#e11d7b]">
            Catégories populaires
          </h2>
          <p className="text-center text-sm text-gray-600 mt-1">
            Découvrez nos offres par catégorie
          </p>

          {topLoading ? (
            <div className="mt-6">
              <Loading />
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center mt-6 text-gray-500">Aucune catégorie</div>
          ) : (
            <div className="relative mt-6">
              {/* prev */}
              <button
                type="button"
                onClick={prevCats}
                disabled={!canPrevCat}
                className="absolute left-1 sm:left-0 top-1/2 -translate-y-1/2 z-10
                           w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white shadow-lg border
                           flex items-center justify-center disabled:opacity-40"
                aria-label="Précédent catégories"
              >
                <FiChevronLeft size={20} className="text-gray-700" />
              </button>

              {/* next */}
              <button
                type="button"
                onClick={nextCats}
                disabled={!canNextCat}
                className="absolute right-1 sm:right-0 top-1/2 -translate-y-1/2 z-10
                           w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white shadow-lg border
                           flex items-center justify-center disabled:opacity-40"
                aria-label="Suivant catégories"
              >
                <FiChevronRight size={20} className="text-gray-700" />
              </button>

              <div className="px-10 sm:px-14">
<div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-6">


                  {visibleCategories.map((cat) => (
<Link
  key={cat._id ?? cat.id}
  to={`/deals?category=${cat.slug ?? cat._id}`}
  className="
    bg-white rounded-2xl
    p-6
    min-h-[120px]
    flex flex-col items-center justify-center
    shadow-md hover:shadow-xl
    transition transform hover:-translate-y-1
    border border-pink-100
  "
  title={cat.name}
>
  <div className="flex justify-center mb-3">
    {renderCategoryIcon(cat.icon, 34, "text-[#e11d7b]")}
  </div>

<div className="text-[13px] sm:text-[14px] font-bold text-gray-800 text-center leading-tight
                min-h-[38px] break-words">
  {cat.name}
</div>


</Link>


                  ))}
                </div>
              </div>

              <div className="mt-4 text-center text-xs text-gray-500">
                {Math.min(catIndex + 1, categories.length)}-
                {Math.min(catIndex + catPerView, categories.length)} / {categories.length} catégories
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Offres en vedette */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-2xl font-bold text-center text-[#e11d7b]">
            Offres en vedette
          </h2>
          <p className="text-center text-sm text-gray-600 mt-1">
            Découvrez nos meilleures offres du moment
          </p>

          {topLoading ? (
            <div className="mt-6">
              <Loading />
            </div>
          ) : featuredDeals.length === 0 ? (
            <div className="text-center mt-6 text-gray-500">Aucune offre en vedette</div>
          ) : (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredDeals.map((deal) => (
                <DealCard key={deal._id ?? deal.id} deal={deal} showAction />
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-center">
            <Link to="/deals" className="text-[#e11d7b] font-semibold hover:underline">
              Voir tous les deals →
            </Link>
          </div>
        </div>
      </section>

      {/* Toutes les offres */}
      <section className="py-10 bg-[#fff3f8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl md:text-2xl font-bold text-center text-[#e11d7b]">
            Toutes les offres
          </h2>
          <p className="text-center text-sm text-gray-600 mt-1">
            Découvrez toutes nos offres disponibles
          </p>

          {allLoading ? (
            <div className="mt-6">
              <Loading />
            </div>
          ) : allDeals.length === 0 ? (
            <div className="text-center mt-6 text-gray-500">Aucune offre disponible</div>
          ) : (
            <>
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
                {allDeals.map((deal) => (
                  <DealCard key={deal._id ?? deal.id} deal={deal} />
                ))}
              </div>

              <div className="mt-6 flex items-center justify-center gap-2">
                <button
                  type="button"
                  onClick={() => goToPage(page - 1)}
                  disabled={page <= 1 || allLoading}
                  className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50 flex items-center gap-1"
                >
                  <FiChevronLeft /> <span>Préc</span>
                </button>

                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }).map((_, idx) => {
                    const start = Math.max(1, Math.min(totalPages - 4, page - 2));
                    const p = start + idx;
                    if (p > totalPages) return null;
                    return (
                      <button
                        key={p}
                        type="button"
                        onClick={() => goToPage(p)}
                        className={`w-9 h-9 rounded-lg border text-sm font-semibold ${
                          p === page
                            ? "bg-[#e11d7b] text-white border-[#e11d7b]"
                            : "bg-white text-gray-700"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={() => goToPage(page + 1)}
                  disabled={page >= totalPages || allLoading}
                  className="px-3 py-2 bg-white border rounded-lg disabled:opacity-50 flex items-center gap-1"
                >
                  <span>Suiv</span> <FiChevronRight />
                </button>
              </div>

              <div className="mt-3 flex justify-center">
                <div className="text-sm text-gray-600 flex items-center gap-2">
                  <span>Afficher</span>
                  <select
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                    className="bg-white border rounded-lg px-2 py-1"
                  >
                    {[6, 9, 12, 18].map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                  <span>/ page</span>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* ✅ Deals par ville */}
      <section id="cities" className="py-14 bg-pink-50/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center text-[#7a1147]">
            Deals par ville
          </h2>
          <p className="text-center text-sm text-gray-500 mb-8">
            Trouve des promotions près de chez toi ou découvre de nouvelles villes.
          </p>

          {citiesWithCount.length === 0 ? (
            <div className="text-center text-gray-500">
              Aucune ville disponible (vérifie que les deals ont bien un champ <b>location</b> ou <b>locations</b>).
            </div>
          ) : (
            <div className="relative">
              <button
                type="button"
                onClick={prevCities}
                disabled={!canPrevCity}
                className="absolute left-1 sm:left-0 top-1/2 -translate-y-1/2 z-10
                           w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white shadow-lg border
                           flex items-center justify-center disabled:opacity-40"
                aria-label="Précédent villes"
              >
                <FiChevronLeft size={20} className="text-gray-700" />
              </button>

              <button
                type="button"
                onClick={nextCities}
                disabled={!canNextCity}
                className="absolute right-1 sm:right-0 top-1/2 -translate-y-1/2 z-10
                           w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white shadow-lg border
                           flex items-center justify-center disabled:opacity-40"
                aria-label="Suivant villes"
              >
                <FiChevronRight size={20} className="text-gray-700" />
              </button>

              <div className="px-10 sm:px-14">
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {visibleCities.map(({ name, count }) => (
                    <Link
                      key={name}
                      to={`/deals?city=${encodeURIComponent(name)}`}
                      className="bg-white p-5 rounded-2xl shadow-sm hover:shadow-lg transition transform hover:-translate-y-1 text-center border border-pink-100"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-10 h-10 rounded-full bg-pink-50 border border-pink-100 flex items-center justify-center">
                          <FiMapPin className="text-[#e11d7b]" />
                        </div>

                        <div className="mt-2 font-semibold text-[#7a1147] text-sm line-clamp-1">
                          {name}
                        </div>
                      </div>

                      <div className="mt-2 text-xs text-gray-500">
                        {count} offre{count > 1 ? "s" : ""}
                      </div>
                    </Link>
                  ))}
                </div>

                <div className="mt-4 text-center text-xs text-gray-500">
                  {Math.min(cityIndex + 1, citiesWithCount.length)}-
                  {Math.min(cityIndex + CITY_PER_VIEW, citiesWithCount.length)} / {citiesWithCount.length} villes
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
